import memoize from 'fast-memoize'
import { EventAssignmentPayload } from 'web/components/fight-segments/EventAssignment'
import { getBuildByPatchInfo, getBuildByVersion } from 'web/data/core-data'
import { CLASS_META } from 'web/data/general/id'
import { WowheadIconNames } from 'web/data/icons'
import { FightSetup, FullFight, Spell } from 'web/data/models'
import { resolveSpellCooldown } from 'web/engine/cooldowns/core-resolver'
import { SpellEngine, uniqueIdFromSpellAssignment } from 'web/engine/spell-engine'
import { sortSpellAssignments } from 'web/utils/sort-utils'
import { expectOneById } from 'web/utils/utils'

/**
 * Helper function to create the eventAssignments callback, requires a lot of
 * similar boilerplate between the display and setup views.
 */
export const createEventAssignmentsCallback = ({
    fullFight,
    selectedSetup,
    onHoverEnter,
    onHoverLeave,
    onClick
}: {
    fullFight: FullFight

    selectedSetup?: FightSetup
    onHoverEnter?: (params: { setupId: string; availableEventIds: string[] }) => void
    onHoverLeave?: (params: { setupId: string }) => void
    onClick?: (saUniqueId: string) => void
}) => {

    const spellEngine: SpellEngine | undefined = (selectedSetup != null)
        ? SpellEngine.fromStates({
            fullFight: fullFight,
            setupState: selectedSetup
        })
        : undefined

    return (eventId: string): EventAssignmentPayload[] => {
        if (selectedSetup == null || spellEngine == null) {
            return []
        }

        const buildData = getBuildByPatchInfo(selectedSetup.patchInfo)

        const filteredAssignments = selectedSetup.spellAssignments
            .filter(assgn => assgn.fightEventId === eventId)

        const eventAssignments: EventAssignmentPayload[] = []

        filteredAssignments
            .sort((a, b) => sortSpellAssignments(buildData, selectedSetup.fightPlayers, a, b))
            .forEach(spellAssignment => {

                const player = expectOneById(selectedSetup.fightPlayers, spellAssignment.playerId)
                const classShortName = CLASS_META[buildData.specsById[player.specId].classId].shortName

                const spell = buildData.spellsById[spellAssignment.spellId]
                const spellCooldown = resolveSpellCooldown(spell.id, player)

                eventAssignments.push({

                    assignmentState: {
                        uniqueId: uniqueIdFromSpellAssignment(spellAssignment),
                        isOverlappingSelf: spellEngine.isAssignmentOverlapping(spellAssignment),
                        tooltipState: spellEngine.getSurroundingInfo(spellAssignment)
                    },

                    playerState: {
                        classShortName,
                        id: player.id,
                        name: player.name
                    },

                    spellState: {
                        id: spell.id,
                        name: spell.name,
                        cooldown: spellCooldown,
                        isUniqueInSetup: isSpellUniqueInSetup(selectedSetup, spell),
                        icon_name: spell.icon_name ?? WowheadIconNames.questionMark,
                    },

                    interactive: {
                        onClick,

                        onHoverEnter: () => {
                            if (spellEngine == null) {
                                throw Error(`Expected a defined SpellEngine`)
                            }

                            const viableEvents = spellEngine
                                .getEventsOutsideAllDeadzones(spellAssignment)

                            if (onHoverEnter) {
                                onHoverEnter({
                                    setupId: selectedSetup.id,
                                    availableEventIds: viableEvents.map(item => item.id)
                                })
                            }
                        },

                        onHoverLeave: () => {
                            if (onHoverLeave) {
                                onHoverLeave({ setupId: selectedSetup.id })
                            }
                        }
                    }
                })

            })

        return eventAssignments
    }

}

/**
 * Checks how many times the given spell occurs in the given setup.
 */
export const isSpellUniqueInSetup = memoize((setup: FightSetup, spell: Spell): boolean => {
    const { name, game_id } = spell
    const build = getBuildByVersion(setup.patchInfo.buildVersion)

    let occurrences = 0

    setup.fightPlayers.forEach(player => {

        const playerSpellIds = build.Spec(player.specId).spellIds

        playerSpellIds.forEach(spellId => {
            /*
                -- DEVELOPER NOTICE --

                We can't do an equality check using spellId, as that's
                just a random GUID representing the spell in the data.

                The only things we have to work with are `game_id`
                and `name`.
            */

            const currentSpell = build.Spell(spellId)

            // Game ID is optional, so we'll try it, but if it fails
            // we'll fall back on the name, it should work most of the
            // time though
            if (game_id != null && game_id === currentSpell.game_id) {
                occurrences++
                return
            }

            // Hopefully there won't be two different spells with the
            // same name in the future lol
            if (name === currentSpell.name) {
                occurrences++
                return
            }

        })

    })

    return occurrences <= 1
})
